import _ from 'lodash';
import { useCallback, useMemo } from 'react';
import {
  CountryCluster,
  CountryMarketConfig,
  CountryMarketConfigList,
  Language,
  Market,
} from '../common/CountryMarketConfig';
import { CountryMarkets } from '../common/Customer';
import { isNotUndefined } from '../common/utils';
import { User } from '../shared/store';
import { CountryMarketUrl } from '../types';
import { useCountryMarketConfigs } from './queries';

export type CountryMarketHelpers = ReturnType<typeof useCountryMarketHelpers>;

/**
 *
 * @param countryMarket string of format 'United Kingdom - Food & Nutrition'
 * @returns object with `country` and `market`
 */
export const splitCountryMarketString = (
  countryMarket: string,
): { country: CountryCluster; market: Market } => {
  const [country, market] = countryMarket
    .split('-')
    .map((arrayItem) => arrayItem.trim());
  return { country: country, market: market as Market };
};

export const getCountryMarketString = (
  countryCluster: CountryCluster,
  market: Market,
): string => `${countryCluster} - ${market}`;

export const getCountryMarketStringByConfig = ({
  countryCluster,
  marketDisplayName,
}: CountryMarketConfig): string =>
  getCountryMarketString(countryCluster, marketDisplayName);

export const isCountryMarketConfigChanged = (
  newCountryMarketConfig: CountryMarketConfig | undefined,
  existingCountryMarketConfig: CountryMarketConfig | undefined | null,
): boolean => !_.isEqual(newCountryMarketConfig, existingCountryMarketConfig);

export const isCountryMarketConfigValidForUser = (
  user: User,
  countryMarketConfig: CountryMarketConfig,
): boolean =>
  Object.values(user.countries_and_markets || {}).some(
    isCountryMarketConfigValidForString.bind(null, countryMarketConfig),
  );

export const isCountryMarketConfigValidForString = (
  countryMarketConfig: CountryMarketConfig | null | undefined,
  countryMarket: unknown,
): boolean =>
  countryMarket ===
  `${countryMarketConfig?.countryCluster} - ${countryMarketConfig?.marketDisplayName}`;

export const redirectTargetByCountryMarketConfig = (
  { eLabCountrySlug, eLabMarketSlug }: Partial<CountryMarketConfig>,
  redirectTarget: string,
  redirectFallback = CountryMarketUrl.Invalid,
): string =>
  eLabCountrySlug && eLabMarketSlug
    ? `/${eLabCountrySlug}/${eLabMarketSlug}/${redirectTarget}`
    : redirectFallback;

export const getDefaultLanguage = ({
  languages,
}: CountryMarketConfig): Language => languages[0];

export const getValidLanguageByUserLanguage = (
  countryMarketConfig: CountryMarketConfig,
  language: Language,
): Language =>
  countryMarketConfig.languages.find((lang) => lang === language) ??
  getDefaultLanguage(countryMarketConfig);


export const useCountryMarketHelpers = () => {
  const countryMarketConfigs = useCountryMarketConfigs();

  const findCountryMarketConfigByURLParams = useCallback(
    (country: string | undefined, market: string | undefined): CountryMarketConfig | undefined =>
      Object.values(countryMarketConfigs ?? []).find(
        (config) =>
          config.eLabCountrySlug === country &&
          config.eLabMarketSlug === market,
      ),
    [countryMarketConfigs],
  );

  const findCountryMarketConfigByCountryAndMarketAbbreviations = useCallback(
    (
      countryAbbreviation: string,
      marketAbbreviation: string,
    ): CountryMarketConfig | undefined =>
      Object.values(countryMarketConfigs ?? []).find(
        (config) =>
          config.datalakeCountryAbbreviation === countryAbbreviation &&
          config.taxonomyMarketAbbreviaton === marketAbbreviation,
      ),
    [countryMarketConfigs],
  );

  const findCountryMarketConfigByCountryAndMarket = useCallback(
    (
      country: CountryCluster,
      market: Market,
    ): CountryMarketConfig | undefined =>
      countryMarketConfigs?.[getCountryMarketString(country, market)],
    [countryMarketConfigs],
  );

  const findCountryMarketConfigByString = useCallback(
    (countryMarket: string): CountryMarketConfig | undefined => {
      const { country, market } = splitCountryMarketString(countryMarket);
      return findCountryMarketConfigByCountryAndMarket(country, market);
    },
    [findCountryMarketConfigByCountryAndMarket],
  );

  const findCountryMarketConfigsByString = useCallback(
    (countryMarkets: string[]): Partial<CountryMarketConfigList> =>
      countryMarkets
        .map((countryMarket) => {
          const { country, market } = splitCountryMarketString(countryMarket);
          const countryMarketConfig = findCountryMarketConfigByCountryAndMarket(
            country,
            market,
          );

          return countryMarketConfig
            ? {
                [country]: { [market]: countryMarketConfig },
              }
            : {};
        })
        .reduce((prev, actual) => _.merge(prev, actual), {}),
    [findCountryMarketConfigByCountryAndMarket],
  );

  /**
   * Split these strings into single properties
   * Then use the country and market to get the config for each
   * accessed country market segment
   */
  const findCountryMarketSlugsByStringsArray = useCallback(
    (
      countriesAndMarkets: CountryMarkets,
    ): {
      country: string;
      market: string;
    }[] =>
      Object.values(countriesAndMarkets)
        .map(findCountryMarketConfigByString)
        .filter(isNotUndefined)
        .map((config) => ({
          country: config.eLabCountrySlug,
          market: config.eLabMarketSlug,
        })),
    [findCountryMarketConfigByString],
  );

  const existsCountryMarketConfigByURLParams = useCallback(
    (country: string, market: string): boolean =>
      !!findCountryMarketConfigByURLParams(country, market),
    [findCountryMarketConfigByURLParams],
  );

  const existsCountryMarketConfigByString = useCallback(
    (countryMarket: string): boolean =>
      !!findCountryMarketConfigByString(countryMarket),
    [findCountryMarketConfigByString],
  );

  const redirectTargetByCountryMarket = useCallback(
    (
      countryMarket: string,
      redirectTarget: string,
      redirectFallback?: CountryMarketUrl,
    ): string => {
      const countryMarketConfig =
        findCountryMarketConfigByString(countryMarket) || {};

      return redirectTargetByCountryMarketConfig(
        countryMarketConfig,
        redirectTarget,
        redirectFallback,
      );
    },
    [findCountryMarketConfigByString],
  );

  const redirectTargetByCountryAndMarket = useCallback(
    (
      country: CountryCluster,
      market: Market,
      redirectTarget: string,
      redirectFallback?: CountryMarketUrl,
    ): string => {
      const countryMarketConfig =
        findCountryMarketConfigByCountryAndMarket(country, market) || {};

      return redirectTargetByCountryMarketConfig(
        countryMarketConfig,
        redirectTarget,
        redirectFallback,
      );
    },
    [findCountryMarketConfigByCountryAndMarket],
  );

  const filterValidCountryMarkets = useCallback(
    (countryMarkets: string[]): string[] =>
      countryMarkets.filter((countryMarket) =>
        existsCountryMarketConfigByString(countryMarket),
      ),
    [existsCountryMarketConfigByString],
  );

  return useMemo(
    () => ({
      ready: !!countryMarketConfigs,
      // members
      existsCountryMarketConfigByString,
      existsCountryMarketConfigByURLParams,
      filterValidCountryMarkets,
      findCountryMarketConfigByCountryAndMarket,
      findCountryMarketConfigByCountryAndMarketAbbreviations,
      findCountryMarketConfigByString,
      findCountryMarketConfigByURLParams,
      findCountryMarketConfigsByString,
      findCountryMarketSlugsByStringsArray,
      redirectTargetByCountryAndMarket,
      redirectTargetByCountryMarket,

      // statics
      getCountryMarketString,
      getDefaultLanguage,
      getValidLanguageByUserLanguage,
      isCountryMarketConfigChanged,
      isCountryMarketConfigValidForString,
      isCountryMarketConfigValidForUser,
      redirectTargetByCountryMarketConfig,
      splitCountryMarketString,
    }),
    [
      countryMarketConfigs,
      existsCountryMarketConfigByString,
      existsCountryMarketConfigByURLParams,
      filterValidCountryMarkets,
      findCountryMarketConfigByCountryAndMarket,
      findCountryMarketConfigByCountryAndMarketAbbreviations,
      findCountryMarketConfigByString,
      findCountryMarketConfigByURLParams,
      findCountryMarketConfigsByString,
      findCountryMarketSlugsByStringsArray,
      redirectTargetByCountryAndMarket,
      redirectTargetByCountryMarket,
    ],
  );
};
